import { c as _c } from "react/compiler-runtime";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import * as React from "react";
import cn from "twsx";

// A document or file viewer: a framed panel (`Viewer`) with a 40px toolbar
// (`ViewerToolbar`) above a sunken canvas (`ViewerCanvas`) that centres a white
// page (`ViewerPage`). `bleed` drops the side and bottom frame for viewers that
// fill a dialog; `scroll` decides whether the canvas or its parent scrolls.
const viewerVariants = cva("flex min-h-0 flex-col border border-border-popover bg-bg-surface", {
  variants: {
    bleed: {
      true: "border-x-0 border-b-0",
      false: "rounded-settings-list-view-item"
    },
    scroll: {
      self: "overflow-hidden",
      parent: "min-h-full flex-none overflow-visible"
    }
  },
  defaultVariants: {
    bleed: false,
    scroll: "self"
  }
});
export type ViewerProps = React.ComponentProps<"div"> & VariantProps<typeof viewerVariants>;

/** Use when showing a document, image or file in a framed viewer with a toolbar. */
export function Viewer(t0) {
  const $ = _c(12);
  let bleed;
  let className;
  let props;
  let scroll;
  if ($[0] !== t0) {
    ({
      bleed,
      scroll,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = bleed;
    $[2] = className;
    $[3] = props;
    $[4] = scroll;
  } else {
    bleed = $[1];
    className = $[2];
    props = $[3];
    scroll = $[4];
  }
  let t1;
  if ($[5] !== bleed || $[6] !== className || $[7] !== scroll) {
    t1 = cn(viewerVariants({
      bleed,
      scroll
    }), className);
    $[5] = bleed;
    $[6] = className;
    $[7] = scroll;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== props || $[10] !== t1) {
    t2 = <div className={t1} {...props} />;
    $[9] = props;
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
}
export function ViewerToolbar(t0) {
  const $ = _c(7);
  const {
    start,
    end
  } = t0;
  let t1;
  if ($[0] !== start) {
    t1 = <div className="flex min-w-0 items-center gap-1">{start}</div>;
    $[0] = start;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== end) {
    t2 = <div className="flex items-center gap-1">{end}</div>;
    $[2] = end;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex min-h-10 items-center justify-between gap-2 border-b border-border-subtle px-3 py-1.5">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
const viewerCanvasVariants = cva("flex-1 bg-bg-secondary", {
  variants: {
    padded: {
      true: "p-6",
      false: "p-0"
    },
    fill: {
      true: "h-full w-full",
      false: ""
    },
    overflow: {
      auto: "overflow-auto",
      hidden: "overflow-hidden",
      visible: "overflow-visible"
    }
  },
  defaultVariants: {
    padded: true,
    fill: false,
    overflow: "auto"
  }
});
export function ViewerCanvas(t0) {
  const $ = _c(14);
  let className;
  let fill;
  let overflow;
  let padded;
  let props;
  if ($[0] !== t0) {
    ({
      padded,
      fill,
      overflow,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = fill;
    $[3] = overflow;
    $[4] = padded;
    $[5] = props;
  } else {
    className = $[1];
    fill = $[2];
    overflow = $[3];
    padded = $[4];
    props = $[5];
  }
  let t1;
  if ($[6] !== className || $[7] !== fill || $[8] !== overflow || $[9] !== padded) {
    t1 = cn(viewerCanvasVariants({
      padded,
      fill,
      overflow
    }), className);
    $[6] = className;
    $[7] = fill;
    $[8] = overflow;
    $[9] = padded;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  let t2;
  if ($[11] !== props || $[12] !== t1) {
    t2 = <div className={t1} {...props} />;
    $[11] = props;
    $[12] = t1;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  return t2;
}

// `scaled` sizes itself from the caller's measured `dimensions` (zoomed HTML);
// `image` fits an image; `frame` is a letter-width embedded file; `letter` is a
// fixed letter-ratio page for a rendered thumbnail; `fill` covers
// a bleed canvas. `asChild` puts the page styling on an <img> or <iframe>.
const viewerPageVariants = cva("mx-auto block bg-white", {
  variants: {
    size: {
      scaled: "[box-shadow:var(--elevation-card)]",
      image: "max-w-full rounded-md [box-shadow:var(--elevation-card)]",
      frame: "h-full min-h-[720px] w-full max-w-4xl rounded-md border-0 [box-shadow:var(--elevation-card)]",
      fill: "h-full min-h-0 w-full border-0",
      letter: "relative aspect-[8.5/11] w-full max-w-[800px] overflow-hidden [box-shadow:var(--elevation-card)]"
    }
  },
  defaultVariants: {
    size: "scaled"
  }
});
export function ViewerPage(t0) {
  const $ = _c(14);
  let asChild;
  let className;
  let dimensions;
  let props;
  let size;
  if ($[0] !== t0) {
    ({
      size,
      asChild,
      dimensions,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = asChild;
    $[2] = className;
    $[3] = dimensions;
    $[4] = props;
    $[5] = size;
  } else {
    asChild = $[1];
    className = $[2];
    dimensions = $[3];
    props = $[4];
    size = $[5];
  }
  const Comp = asChild ? Slot : "div";
  let t1;
  if ($[6] !== className || $[7] !== size) {
    t1 = cn(viewerPageVariants({
      size
    }), className);
    $[6] = className;
    $[7] = size;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== Comp || $[10] !== dimensions || $[11] !== props || $[12] !== t1) {
    t2 = <Comp className={t1} style={dimensions} {...props} />;
    $[9] = Comp;
    $[10] = dimensions;
    $[11] = props;
    $[12] = t1;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  return t2;
}

// Covers the page or thumbnail it sits in: a loading skeleton or a centred
// status message over an absent preview.
const viewerOverlayVariants = cva("absolute inset-0 flex overflow-hidden text-center", {
  variants: {
    content: {
      center: "items-center justify-center",
      stretch: "flex-col"
    },
    padding: {
      none: "p-0",
      sm: "p-3",
      md: "p-6"
    }
  },
  defaultVariants: {
    content: "center",
    padding: "none"
  }
});
export function ViewerOverlay(t0) {
  const $ = _c(10);
  let content;
  let padding;
  let props;
  if ($[0] !== t0) {
    ({
      content,
      padding,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = content;
    $[2] = padding;
    $[3] = props;
  } else {
    content = $[1];
    padding = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== content || $[5] !== padding) {
    t1 = viewerOverlayVariants({
      content,
      padding
    });
    $[4] = content;
    $[5] = padding;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== props || $[8] !== t1) {
    t2 = <div className={t1} {...props} />;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}